import { env } from '@bb/env';

type RouteArg<T> = string | ((args: T) => string);
export type ServerResponse<T> = { data: T };
export type PaginatedResponse<T> = {
  data: T;
  total?: number;
  nextCursor?: string;
};

export type PaginationQuery = { cursor?: string; perPage?: number };

type QueryParams = Record<
  string,
  string | boolean | number | string[] | undefined
>;

interface CacheConfig {
  type: RequestCache;
  revalidate:
    | false // caches indefinitely
    | 0 // prevents caching
    | number; // in seconds
  tags?: string[];
}
interface ClientInput {
  args?: Record<string, unknown>;
  query?: QueryParams;
  payload?: unknown;
}

const handleFetchResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorMessage = await response.text();
    throw new Error(errorMessage || response.statusText);
  }

  return response.json();
};

export abstract class AbstractClient {
  protected apiRoute: string;

  private getUrl(route: string, query?: QueryParams): URL {
    const url = new URL(this.apiRoute + route, env.NEXT_PUBLIC_API_ROOT_URL);

    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null) continue;
        url.searchParams.append(key, String(value));
      }
    }

    return url;
  }

  constructor(baseURL: string) {
    this.apiRoute = `/api${baseURL}`;
  }

  protected get<RES, REQ extends Omit<ClientInput, 'payload'>>(
    route: RouteArg<REQ['args']>
    // baseQuery?: Record<string, any>
  ) {
    return async (input?: REQ & { cache?: CacheConfig }): Promise<RES> => {
      const urlEnding = typeof route === 'string' ? route : route(input?.args);
      const url = this.getUrl(urlEnding, input?.query);

      const response = await fetch(
        url,
        input?.cache
          ? {
              cache: input.cache.type,
              next: input.cache
                ? {
                    revalidate: input.cache.revalidate,
                    tags: input.cache.tags,
                  }
                : undefined,
            }
          : undefined
      );
      return handleFetchResponse<RES>(response);
    };
  }

  protected payloadHandler<RES, REQ extends Omit<ClientInput, 'cache'>>(
    method: 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    route: RouteArg<REQ['args']>
  ) {
    return async (input: REQ): Promise<RES> => {
      const urlEnding = typeof route === 'string' ? route : route(input?.args);
      const url = this.getUrl(urlEnding);

      const response = await fetch(url, {
        method,
        body: JSON.stringify(input.payload),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return handleFetchResponse<RES>(response);
    };
  }
}
